import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MessageSquare, FileText, Ruler, Hammer, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const steps = [
  {
    icon: MessageSquare,
    title: "Beratung",
    description: "Persönliches Erstgespräch und umfassende Bedarfsanalyse. Wir hören zu, verstehen Ihre Anforderungen und entwickeln erste Konzeptideen.",
    detail: "Kostenlos & unverbindlich",
    number: "01",
  },
  {
    icon: FileText,
    title: "Angebot",
    description: "Detaillierte Kalkulation und transparentes Angebot. Keine versteckten Kosten, klare Preisstruktur und faire Konditionen.",
    detail: "Transparent & fair",
    number: "02",
  },
  {
    icon: Ruler,
    title: "Planung",
    description: "Präzise Projektplanung mit klar definierten Meilensteinen, Zeiträumen und Verantwortlichkeiten für einen reibungslosen Ablauf.",
    detail: "Präzise & strukturiert",
    number: "03",
  },
  {
    icon: Hammer,
    title: "Ausführung",
    description: "Fachgerechte Umsetzung durch unsere eigenen qualifizierten Teams. Laufende Qualitätskontrolle und regelmäßige Statusberichte.",
    detail: "Professionell & pünktlich",
    number: "04",
  },
  {
    icon: CheckCircle,
    title: "Abnahme",
    description: "Gemeinsame Endabnahme, detaillierte Dokumentation und sorgfältige Übergabe. Ihre Zufriedenheit ist unser oberster Maßstab.",
    detail: "Zufriedenheitsgarantie",
    number: "05",
  },
];

export const ProzessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="relative section-padding bg-card overflow-hidden" ref={ref}>
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-5"
        style={{ backgroundImage: `url(/images/warum-bg.jpg)` }}
      />

      <div className="container-wide relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              So arbeiten wir
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
              Fünf Schritte zum Erfolg.
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              Unser bewährter Prozess sorgt für einen reibungslosen Ablauf – von der ersten Beratung bis zur finalen Abnahme. 
              Transparent, strukturiert und immer auf Ihre Bedürfnisse zugeschnitten.
            </p>
            <div className="flex gap-0.5 mt-6">
              <div className="w-12 h-1 gradient-blue" />
              <div className="w-12 h-1 gradient-orange" />
              <div className="w-12 h-1 gradient-green" />
            </div>
          </motion.div>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-border">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.12 }}
              >
                <Card className="border-0 bg-background h-full flex flex-col">
                  <CardContent className="p-6 md:p-8 flex flex-col flex-1">
                    {/* Step number + icon */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-heading font-bold text-3xl text-foreground/10">
                        {step.number}
                      </span>
                      <div className="p-2.5 gradient-blue-subtle border border-brand-blue/20">
                        <Icon className="w-5 h-5 text-brand-blue-light" />
                      </div>
                    </div>

                    <h3 className="font-heading font-semibold text-base text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground font-body leading-relaxed mb-4 flex-1">
                      {step.description}
                    </p>
                    <span className="text-[10px] font-heading uppercase tracking-widest text-brand-blue-light bg-brand-blue-muted px-3 py-1.5 self-start">
                      {step.detail}
                    </span>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
